
// The AEP recommendation builder sidebar (#721, spec §4.2, §7, §11, §12).
// Reads the store; every change goes through commit() (the D5 reset rule).
import _ from 'lodash'
import { mapGetters } from 'vuex'
import { cheaperPdps, pdpCostWarningText } from '~/util/aep-cost-check'
import * as aep from '~/util/aep-recommendation'
import { LINES_BY_COVERAGE } from '~/util/aep-current-plans'
import { planOnFile, planQueryRe } from '~/util/summary-plans'
import { money } from '~/util/string'

const KIND_TYPES = { supp: ['supp'], pdp: ['pdp'], mapd: ['mapd', 'ma'] }
const KIND_LABEL = { supp: 'SUPP', pdp: 'PDP', mapd: 'MAPD' }
const PLAN_TYPE = p => (p.mapd_or_ma || p.type === 'ma' || p.plan_type === 'ma' ? 'mapd' : (p.type || p.plan_type))

export default {
    data () {
        return {
            renderKey: 0,
            coverageOptions: [
                { value: 'SUPP_PDP', text: 'Original Medicare + a Medigap and a drug plan' },
                { value: 'PDP', text: 'Original Medicare + a drug plan only' },
                { value: 'MAPD', text: 'A Medicare Advantage plan' },
            ],
        }
    },
    computed: {
        ...mapGetters([
            'survey', 'summary', 'summaryMode', 'summaryRecommendation', 'summaryDataCards', 'summaryPlansList', 'summaryPlansMap',
            'plans', 'plansMap', 'aepPayer', 'aepCoverage', 'aepCurrentGuess', 'aepCurrent', 'aepContext', 'aepLines', 'aepEnginePlansById',
            'summaryCombosPlans', 'isPlanCommissionable', 'isPlanEnrollable', 'year', 'aepCurrentUnresolved', 'aepCurrentNotices',
        ]),
        isLegacy () {
            return !this.summaryRecommendation && !!this.summaryMode
        },
        // The proposal for a summary without a recommendation (spec §11): the
        // legacy mode + combo + the survey guess. Nothing is written until the
        // advisor confirms coverage or picks a preset.
        derived () {
            const combo = (this.summaryCombosPlans || []).map(p => ({ id: p.id, type: PLAN_TYPE(p) }))
            return aep.deriveFromLegacy(this.summaryMode, combo, this.aepCurrentGuess, this.survey?.current_medicare_coverage)
        },
        rec () {
            return this.summaryRecommendation || this.derived
        },
        coverage () {
            return this.aepCoverage
        },
        suppLeftOut () {
            return aep.suppLeftOut(this.coverage, this.rec, this.aepCurrent)
        },
        kinds () {
            return LINES_BY_COVERAGE[this.coverage] || []
        },
        verified () {
            return !!this.summaryRecommendation?.current?.verified
        },
        surveyCoverage () {
            const c = this.survey?.current_medicare_coverage
            return aep.COVERAGES.includes(c) ? c : null
        },
        surveyCoverageMismatch () {
            return !!this.summaryRecommendation && !!this.surveyCoverage && this.surveyCoverage !== this.coverage
        },
        initial () {
            return aep.isInitial(this.coverage, this.rec)
        },
        presets () {
            return aep.PRESETS_BY_COVERAGE[this.coverage] || []
        },
        presetValue () {
            if (this.initial) { return 'initial' }
            return this.presetValueOf(this.rec)
        },
        scenario () {
            return aep.scenario(this.coverage, this.rec)
        },
        suppApp () {
            return aep.suppApp(this.coverage, this.rec)
        },
        upgrade () {
            return aep.suppUpgrade(this.coverage, this.rec, this.aepCurrent, this.aepEnginePlansById)
        },
        reasonOptions () {
            const allowed = aep.allowedReasons(this.scenario.mode, this.upgrade)
            return aep.REASONS.filter(r => allowed.includes(r.id))
        },
        reasons () {
            return aep.effectiveReasons(this.rec, this.scenario.mode, this.upgrade)
        },
        // Option 1's resulting drug plan (kept or picked) when cheaper PDPs
        // exist in this year's quotes: util/aep-cost-check.js. '' otherwise.
        pdpCostWarning () {
            const line = (this.aepLines || []).find(l => l.plan && l.plan.type === 'pdp')
            const plan = line && (this.plansMap[line.plan.id] || this.summaryPlansMap[line.plan.id])
            if (!plan) { return '' }
            return pdpCostWarningText(plan, cheaperPdps(plan, this.plans), this.year, this.reasons.includes('rx'))
        },
        underwriting () {
            return this.rec.underwriting || aep.defaultUnderwriting(this.coverage, this.rec, this.reasons)
        },
        // ---- options for the client (round-2 spec §13.3) ----
        alternatives () {
            return this.rec.alternatives || []
        },
        // an alternative must change something: staying is the fallback in next steps
        altPresets () {
            return this.presets.filter(p => !(p.medical === 'keep' && p.drug === 'keep'))
        },
        // Spec §12: why a verified, non-Initial scenario has no lines.
        warnings () {
            if (!this.verified || this.initial || this.aepLines.length) { return [] }
            const out = []
            const cur = this.rec.current || {}
            const needCurrent = { pdp: this.coverage !== 'MAPD' && this.rec.drug === 'keep' && this.rec.medical !== 'to_mapd', supp: this.coverage === 'SUPP_PDP' && this.rec.medical === 'keep', mapd: this.coverage === 'MAPD' && this.rec.medical === 'keep' }
            Object.keys(needCurrent).forEach((kind) => {
                if (needCurrent[kind] && !this.summaryPlansMap[cur[`${kind}_id`]]) {
                    const g = this.guessFor(kind)
                    out.push(g
                        ? `Current ${KIND_LABEL[kind]} plan is ${cur[`${kind}_id`] == null ? 'not set' : 'missing from the Plans list'}; the survey resolves to ${g.quote.name}, use it above.`
                        : `Current ${KIND_LABEL[kind]} plan is missing from the Plans list (${this.reasonText(kind)}); pick it above or add it.`)
                }
            })
            const needNew = this.neededNewKinds()
            Object.keys(needNew).forEach((kind) => {
                if (!needNew[kind]) { return }
                const id = this.rec[`${kind}_id`]
                if (id == null) {
                    out.push(`Pick the new ${KIND_LABEL[kind]} plan below, or add one under Potential Plans.`)
                } else if (!this.summaryPlansMap[id]) {
                    out.push(`The chosen ${KIND_LABEL[kind]} plan (id ${id}) was removed from the Plans list; add it back or pick another plan.`)
                }
            })
            return out
        },
    },
    watch: {
        summaryPlansList () {
            this.fillEmptyNewPlans()
        },
    },
    methods: {
        kindLabel (kind) {
            return KIND_LABEL[kind]
        },
        // A new-plan slot is empty when it has no id, or its plan left the
        // Plans list (browser QA 2026-09-29).
        slotEmpty (kind) {
            const id = this.rec[`${kind}_id`]
            return id == null || !this.summaryPlansMap[id]
        },
        // Which kinds the scenario needs a NEW plan for.
        neededNewKinds () {
            return this.neededNewKindsOf(this.rec)
        },
        neededNewKindsOf (r) {
            const needNew = { pdp: r.drug === 'switch' || r.medical === 'to_supp', supp: r.medical === 'switch' && this.coverage === 'SUPP_PDP', mapd: (r.medical === 'switch' && this.coverage === 'MAPD') || r.medical === 'to_mapd' }
            if (r.medical === 'to_supp') { needNew.supp = true }
            return needNew
        },
        // The advisor picked a scenario before adding the plan it needs (Aziz
        // 2026-09-29): once a potential plan of that kind exists, it fills the
        // empty slot, like pickPreset does when the plan is already there.
        fillEmptyNewPlans () {
            if (!this.summaryRecommendation || this.initial) { return false }
            const cur = this.rec.current || {}
            const needNew = this.neededNewKinds()
            const patch = {}
            Object.keys(needNew).forEach((kind) => {
                if (!needNew[kind] || !this.slotEmpty(kind)) { return }
                const id = this.plansOfKind(kind, this.summaryPlansList).map(x => x.id).find(x => x !== cur[`${kind}_id`])
                if (id != null) { patch[`${kind}_id`] = id }
            })
            return Object.keys(patch).length ? this.commit(patch) : false
        },
        coverageLabel (coverage) {
            return this.coverageOptions.find(o => o.value === coverage)?.text || coverage
        },
        // The store's guess for a line when a recommendation is saved and the
        // guess is not the saved id (null, or the client re-answered).
        guessFor (kind) {
            if (!this.summaryRecommendation || !this.verified) { return null }
            const saved = this.summaryRecommendation.current?.[`${kind}_id`]
            const g = this.aepCurrentGuess?.[kind]
            return g?.quote && g.id !== saved ? g : null
        },
        useGuess (kind) {
            const g = this.guessFor(kind)
            return g ? this.setCurrent(kind, g.id) : false
        },
        // Start over on the survey's coverage: nothing verified, no lines, no
        // reasons; the note is kept. Goes through commit() (D5 confirm).
        rebuildFromSurvey () {
            return this.commit({
                coverage: this.surveyCoverage,
                medical: null,
                drug: null,
                supp_id: null,
                pdp_id: null,
                mapd_id: null,
                reasons: [],
                alternatives: [],
                current: { supp_id: null, pdp_id: null, mapd_id: null, verified: false, notices: [] },
            })
        },
        // The resolver's terminated / merged verdicts, frozen with the verified
        // ids so every render of the letter (client page, PDF, email) has them.
        savedNotices () {
            return Array.isArray(this.aepCurrentNotices) ? this.aepCurrentNotices.map(n => ({ ...n })) : []
        },
        plansOfKind (kind, list) {
            return (list || []).filter(p => KIND_TYPES[kind].includes(PLAN_TYPE(p)))
        },
        // The carrier and plan name on file. The renewal step stores a label
        // that already starts with a one-word carrier ("Wellcare Value Script
        // (PDP)"), so the carrier is shown once (round 2 ruling).
        surveyLine (kind) {
            return planOnFile(this.survey, kind)
        },
        unresolved (kind) {
            return typeof this.aepCurrentUnresolved === 'function' ? this.aepCurrentUnresolved(kind) : null
        },
        // Spec §5.2: one short line per reason; advisor-facing only (D24).
        reasonText (kind) {
            const u = this.unresolved(kind)
            const y = this.year
            if (!u) { return "Not found in this year's quotes" }
            return {
                terminated: `Not offered in ${y}`,
                consolidated_missing: 'Merged into a plan not sold in this county',
                not_offered: `Not sold in this county in ${y}`,
                not_identified: "Not found in this year's quotes",
            }[u.reason] || "Not found in this year's quotes"
        },
        currentRow (kind) {
            const saved = this.summaryRecommendation?.current?.[`${kind}_id`]
            // Saved ids once the advisor verified; before that (a fresh summary,
            // or right after "Rebuild from the survey") the store's guess.
            if (this.summaryRecommendation && this.verified) {
                const plan = this.summaryPlansMap[saved] || this.plansMap[saved] || null
                return { id: plan ? saved : null, plan, confidence: null, merged: false }
            }
            const g = this.aepCurrentGuess?.[kind]
            const id = g?.id ?? this.derived.current?.[`${kind}_id`] ?? null
            const plan = this.summaryPlansMap[id] || this.plansMap[id] || null
            return { id: plan ? id : null, plan, confidence: g?.confidence || null, merged: !!g?.merged }
        },
        currentOptions (kind) {
            return [{ value: null, text: 'Not in the Plans list' }].concat(this.plansOfKind(kind, this.summaryPlansList).map(p => ({ value: p.id, text: p.name })))
        },
        // The row's plan when it is in the Plans list, else the "Not in the
        // Plans list" option (a plan resolved from the quotes gets added to the
        // list on Confirm / Use it).
        selectValue (kind) {
            const id = this.currentRow(kind).id
            return id != null && this.summaryPlansMap[id] ? id : null
        },
        searchQuotes (kind) {
            return ({ query }) => {
                const re = planQueryRe(query)
                return { items: this.plansOfKind(kind, this.plans).filter(p => !query || re.test(p.name) || re.test(p.carrier)) }
            }
        },
        async ensureInPlansList (id) {
            if (id != null && !this.summaryPlansMap[id] && this.plansMap[id]) {
                await this.$store.dispatch('addSummaryPlan', this.plansMap[id])
            }
        },
        async addAndSetCurrent (kind, plan) {
            if (!plan) { return }
            await this.ensureInPlansList(plan.id)
            return this.setCurrent(kind, plan.id)
        },
        async setCurrent (kind, id) {
            // undefined = BootstrapVue reporting a select with no selected option
            // (Vue's synthetic change), never an advisor's pick.
            if (id === undefined) { return false }
            await this.ensureInPlansList(id)
            const patch = { coverage: this.coverage, current: { [`${kind}_id`]: id, verified: true, notices: this.savedNotices() } }
            // the plan the client has cannot also be the plan we switch them to
            if (id != null && this.rec[`${kind}_id`] === id) { patch[`${kind}_id`] = null }
            return this.commit(patch)
        },
        async verifyCurrent () {
            const current = { verified: true, notices: this.savedNotices() }
            for (const kind of this.kinds) {
                const row = this.currentRow(kind)
                await this.ensureInPlansList(row.id)
                current[`${kind}_id`] = row.id
            }
            return this.commit({ coverage: this.coverage, current })
        },
        pickPreset (p) {
            if (!p) { return this.commit({ medical: null, drug: null }) }
            const patch = { medical: p.medical, drug: p.drug }
            const cur = this.rec.current || {}
            const firstNonCurrent = kind => this.plansOfKind(kind, this.summaryPlansList).map(x => x.id).find(id => id !== cur[`${kind}_id`]) ?? null
            const empty = kind => this.slotEmpty(kind)
            if (p.drug === 'switch' && empty('pdp')) { patch.pdp_id = firstNonCurrent('pdp') }
            if (p.medical === 'to_supp') {
                if (empty('supp')) { patch.supp_id = firstNonCurrent('supp') }
                if (empty('pdp')) { patch.pdp_id = firstNonCurrent('pdp') }
            }
            if (p.medical === 'switch' && this.coverage === 'SUPP_PDP' && empty('supp')) { patch.supp_id = firstNonCurrent('supp') }
            if ((p.medical === 'switch' && this.coverage === 'MAPD') || p.medical === 'to_mapd') {
                if (empty('mapd')) { patch.mapd_id = firstNonCurrent('mapd') }
            }
            return this.commit(patch)
        },
        lineValue (kind) {
            return this.lineValueOf(this.rec, kind)
        },
        lineValueOf (r, kind) {
            if (kind === 'pdp') { return r.drug === 'switch' ? `sw:${r.pdp_id}` : 'keep' }
            if (r.medical === 'to_supp' || r.medical === 'to_mapd') { return r.medical }
            return r.medical === 'switch' ? `sw:${r[`${kind}_id`]}` : 'keep'
        },
        lineOptions (kind, extra) {
            const cur = this.rec.current?.[`${kind}_id`]
            const curPlan = this.summaryPlansMap[cur]
            const opts = []
            if (curPlan) { opts.push({ value: 'keep', text: `Keep current: ${curPlan.name}` }) }
            this.plansOfKind(kind, this.summaryPlansList).filter(p => p.id !== cur).forEach((p) => {
                const flags = [!this.isPlanCommissionable(p) && 'non-comm', !this.isPlanEnrollable(p) && "can't enroll"].filter(Boolean)
                opts.push({ value: `sw:${p.id}`, text: `${curPlan ? 'Switch to: ' : ''}${p.name} (${money(p.monthly_premium)}/mo)${flags.length ? ' · ' + flags.join(', ') : ''}` })
            })
            if (extra) { opts.push({ value: extra[0], text: extra[1] }) }
            return opts
        },
        setLine (kind, value) {
            if (value === 'to_supp' || value === 'to_mapd') { return this.commit({ medical: value }) }
            const id = /^sw:/.test(value) ? parseInt(value.slice(3), 10) : null
            if (kind === 'pdp') { return this.commit(id ? { drug: 'switch', pdp_id: id } : { drug: 'keep' }) }
            return this.commit(id ? { medical: 'switch', [`${kind}_id`]: id } : { medical: 'keep' })
        },
        newPlanOptions (kind) {
            const cur = this.rec.current?.[`${kind}_id`]
            return [{ value: null, text: 'Select a plan' }].concat(this.plansOfKind(kind, this.summaryPlansList).filter(p => p.id !== cur).map(p => ({ value: p.id, text: `${p.name} (${money(p.monthly_premium)}/mo)` })))
        },
        toggleReason (id) {
            const set = new Set(this.reasons)
            if (set.has(id)) { set.delete(id) } else { set.add(id) }
            return this.commit({ reasons: [...set] })
        },
        commitNote (value) {
            if ((value || '') !== (this.rec.note || '')) { return this.commit({ note: value || '' }) }
        },
        // ---- options for the client (round-2 spec §13.3) ----
        altRec (alt) {
            return aep.scenarioRec(this.rec, alt)
        },
        presetKey (p) {
            return `p${this.presets.indexOf(p)}`
        },
        presetValueOf (r) {
            const i = this.presets.findIndex((p) => {
                if (this.coverage === 'PDP') { return p.drug === r.drug }
                if (this.coverage === 'MAPD') { return p.medical === r.medical }
                return p.medical === r.medical && (p.medical === 'to_mapd' || p.drug === r.drug)
            })
            return i === -1 ? null : `p${i}`
        },
        underwritingOf (alt) {
            return alt.underwriting || aep.defaultUnderwriting(this.coverage, this.altRec(alt), [])
        },
        // The first potential plan of a kind that no option uses yet (the
        // current plan counts as used), else the first non-current one.
        unusedPlanId (kind, exceptIndex = -1) {
            const cur = this.rec.current?.[`${kind}_id`]
            const used = [cur, this.rec[`${kind}_id`]].concat(this.alternatives.filter((a, j) => j !== exceptIndex).map(a => a[`${kind}_id`])).filter(id => id != null)
            const ids = this.plansOfKind(kind, this.summaryPlansList).map(p => p.id)
            return ids.find(id => !used.includes(id)) ?? ids.find(id => id !== cur) ?? null
        },
        // A preset's acts, plus a plan for every new-plan slot that is empty.
        presetPatch (p, r, index) {
            const patch = { medical: p.medical, drug: p.drug }
            const empty = kind => r[`${kind}_id`] == null || !this.summaryPlansMap[r[`${kind}_id`]]
            if (p.drug === 'switch' && empty('pdp')) { patch.pdp_id = this.unusedPlanId('pdp', index) }
            if (p.medical === 'to_supp') {
                if (empty('supp')) { patch.supp_id = this.unusedPlanId('supp', index) }
                if (empty('pdp')) { patch.pdp_id = this.unusedPlanId('pdp', index) }
            }
            if (p.medical === 'switch' && this.coverage === 'SUPP_PDP' && empty('supp')) { patch.supp_id = this.unusedPlanId('supp', index) }
            if ((p.medical === 'switch' && this.coverage === 'MAPD') || p.medical === 'to_mapd') {
                if (empty('mapd')) { patch.mapd_id = this.unusedPlanId('mapd', index) }
            }
            return patch
        },
        pickPresetFor (i, p) {
            return this.commitAlternative(i, this.presetPatch(p, this.alternatives[i], i))
        },
        setLineFor (i, kind, value) {
            if (value === 'to_supp' || value === 'to_mapd') { return this.commitAlternative(i, { medical: value }) }
            const id = /^sw:/.test(value) ? parseInt(value.slice(3), 10) : null
            if (kind === 'pdp') { return this.commitAlternative(i, id ? { drug: 'switch', pdp_id: id } : { drug: 'keep' }) }
            return this.commitAlternative(i, id ? { medical: 'switch', [`${kind}_id`]: id } : { medical: 'keep' })
        },
        addAlternative () {
            const blank = { medical: null, drug: null, supp_id: null, pdp_id: null, mapd_id: null, household_discount: false, underwriting: null }
            const p = this.altPresets[0]
            const alt = p ? { ...blank, ...this.presetPatch(p, blank, this.alternatives.length) } : blank
            return this.commit({ alternatives: this.alternatives.concat([alt]) }, { option: this.alternatives.length + 2 })
        },
        removeAlternative (i) {
            return this.commit({ alternatives: this.alternatives.filter((a, j) => j !== i) }, { option: i + 2 })
        },
        commitAlternative (i, patch) {
            return this.commit({ alternatives: this.alternatives.map((a, j) => (j === i ? { ...a, ...patch } : a)) }, { option: i + 2 })
        },
        // The plan ids an option resolves to; [] when it is incomplete.
        optionIds (r) {
            return aep.lines(this.coverage, r, this.aepCurrent, this.aepEnginePlansById).map(l => l.plan.id)
        },
        alternativeWarnings (i) {
            const n = i + 2
            const r = this.altRec(this.alternatives[i])
            if (aep.isInitial(this.coverage, r)) { return [`Option ${n}: pick a scenario.`] }
            const ids = this.optionIds(r)
            if (!ids.length) {
                const out = []
                const needNew = this.neededNewKindsOf(r)
                Object.keys(needNew).forEach((kind) => {
                    if (!needNew[kind]) { return }
                    const id = r[`${kind}_id`]
                    if (id == null) {
                        out.push(`Option ${n}: pick the new ${KIND_LABEL[kind]} plan, or add one under Potential Plans.`)
                    } else if (!this.summaryPlansMap[id]) {
                        out.push(`Option ${n}: the chosen ${KIND_LABEL[kind]} plan (id ${id}) was removed from the Plans list; pick another plan.`)
                    }
                })
                return out.length ? out : [`Option ${n} is incomplete.`]
            }
            const same = a => a.length === ids.length && a.every(id => ids.includes(id))
            if (same(this.optionIds(this.rec))) { return [`Option ${n} is the same as Option 1.`] }
            for (let j = 0; j < i; j++) {
                if (same(this.optionIds(this.altRec(this.alternatives[j])))) { return [`Option ${n} is the same as Option ${j + 2}.`] }
            }
            return []
        },
        // combo card key per option number, from the engine context
        optionKeys () {
            const map = {}
            aep.options(this.aepContext).forEach((o) => { map[o.option] = aep.comboKey(o.lines) })
            return map
        },
        // D5, refined (round-2 spec §13.3): a change to Option n resets the
        // generated cards and Option n's own card; a change to the current
        // plans or the coverage resets every option card and Current Plan
        // Costs; adding or removing an option resets Option 1 too (its
        // opening sentence counts the options).
        resetKeys (patch, option) {
            const all = 'current' in patch || 'coverage' in patch
            const keys = aep.GENERATED_CARDS.filter(k => k !== 'current_plan_costs' || all)
            const byOption = this.optionKeys()
            if (all) {
                Object.values(byOption).forEach(k => keys.push(k))
            } else {
                const own = option || 1
                if (byOption[own]) { keys.push(byOption[own]) }
                const n = Array.isArray(patch.alternatives) ? patch.alternatives.length : this.alternatives.length
                if (own !== 1 && n !== this.alternatives.length && byOption[1]) { keys.push(byOption[1]) }
            }
            return _.uniq(keys.filter(Boolean))
        },
        generatedEdited (patch = {}, option) {
            return this.resetKeys(patch, option).filter(k => this.summaryDataCards?.[k]?.html)
        },
        // D5: confirm before a change that would hide hand edits; nothing is
        // written on cancel. A legacy summary's first change writes the whole
        // derived proposal (spec §11).
        async commit (patch, { option } = {}) {
            const edited = this.generatedEdited(patch, option)
            if (edited.length) {
                const ok = await this.$bvModal.msgBoxConfirm(`Changing the recommendation resets the text of ${edited.length} edited card(s): ${edited.join(', ')}. Continue?`, {
                    title: 'Reset edited cards?', okVariant: 'danger', okTitle: 'Yes, reset them', cancelTitle: 'Cancel', centered: true,
                })
                if (!ok) {
                    this.renderKey += 1
                    return false
                }
                // keep checked/config, like Summary.vue's onHtmlReset (review M7)
                edited.forEach(card => this.$store.commit('summaryDataCard', { card, ...this.summaryDataCards[card], html: null }))
            }
            const full = this.summaryRecommendation ? patch : _.merge({}, this.derived, patch)
            this.$store.commit('summaryRecommendation', full)
            this.$store.commit('summaryManual', true)
            return true
        },
    },
}
